import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtError, eur } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Wrench } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nom: "", categorie: "engin", marque: "", modele: "", immatriculation: "", annee: "",
  propriete_location: "propriété", cout_journalier: 0, cout_horaire: 0, disponibilite: "disponible",
  localisation: "", assurance_expiration: "", controle_expiration: "", maintenance_notes: "" };

const DISPO_COLORS = {
  disponible: "bg-emerald-500", affecté: "bg-sky-500", maintenance: "bg-amber-500", immobilisé: "bg-red-500",
};

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/equipment").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new")) { setOpen(true); setParams({}, { replace: true }); } }, [params, setParams]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/equipment", { ...form, cout_journalier: parseFloat(form.cout_journalier) || 0, cout_horaire: parseFloat(form.cout_horaire) || 0 });
      toast.success("Matériel ajouté");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="equipment-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Matériel & Véhicules</h1>
        <Button data-testid="new-equipment-button" onClick={() => setOpen(true)} className="bg-amber-500 text-slate-900 font-bold hover:bg-amber-400 h-12">
          <Plus className="h-5 w-5 mr-1" /> Nouveau
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="equipment-list">
        {items.map((e) => (
          <div key={e.id} className="bg-card rounded-xl border-2 border-border p-4" data-testid={`equipment-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-amber-600" />
                <div className="font-bold text-sm">{e.nom}</div>
              </div>
              <span className={`${DISPO_COLORS[e.disponibilite] || "bg-slate-400"} text-white text-[10px] font-bold px-2 py-1 rounded uppercase`}>{e.disponibilite}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{e.categorie} {e.marque && `— ${e.marque} ${e.modele}`} {e.immatriculation && `— ${e.immatriculation}`}</div>
            {e.localisation && <div className="text-xs mt-1">📍 {e.localisation}</div>}
            <div className="flex gap-4 mt-2 text-xs font-mono-num font-bold">
              {e.cout_journalier > 0 && <span>{eur(e.cout_journalier)}/j</span>}
              {e.cout_horaire > 0 && <span>{eur(e.cout_horaire)}/h</span>}
            </div>
            {(e.assurance_expiration || e.controle_expiration) && (
              <div className="text-[10px] text-muted-foreground mt-2">
                {e.assurance_expiration && <div>Assurance : {e.assurance_expiration}</div>}
                {e.controle_expiration && <div>Contrôle : {e.controle_expiration}</div>}
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground col-span-full" data-testid="equipment-empty">Aucun matériel enregistré.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="equipment-form-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Nouveau matériel</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="eq-nom" required placeholder="Nom / désignation *" value={form.nom} onChange={(e) => set("nom", e.target.value)} className="py-3.5" />
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="eq-categorie" value={form.categorie} onChange={(e) => set("categorie", e.target.value)} className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                <option value="engin">Engin</option><option value="véhicule">Véhicule</option><option value="matériel">Matériel</option><option value="outillage">Outillage</option>
              </select>
              <select data-testid="eq-propriete" value={form.propriete_location} onChange={(e) => set("propriete_location", e.target.value)} className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                <option value="propriété">Propriété</option><option value="location">Location</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="eq-marque" placeholder="Marque" value={form.marque} onChange={(e) => set("marque", e.target.value)} />
              <Input data-testid="eq-modele" placeholder="Modèle" value={form.modele} onChange={(e) => set("modele", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="eq-immat" placeholder="Immatriculation / n° série" value={form.immatriculation} onChange={(e) => set("immatriculation", e.target.value)} />
              <Input data-testid="eq-annee" placeholder="Année" value={form.annee} onChange={(e) => set("annee", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="eq-cout-j" type="number" step="any" placeholder="Coût journalier €" value={form.cout_journalier} onChange={(e) => set("cout_journalier", e.target.value)} />
              <Input data-testid="eq-cout-h" type="number" step="any" placeholder="Coût horaire €" value={form.cout_horaire} onChange={(e) => set("cout_horaire", e.target.value)} />
            </div>
            <Input data-testid="eq-localisation" placeholder="Localisation / dépôt" value={form.localisation} onChange={(e) => set("localisation", e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] font-bold uppercase text-muted-foreground">Assurance (expiration)</label>
                <Input data-testid="eq-assurance" type="date" value={form.assurance_expiration} onChange={(e) => set("assurance_expiration", e.target.value)} /></div>
              <div><label className="text-[10px] font-bold uppercase text-muted-foreground">Contrôle (expiration)</label>
                <Input data-testid="eq-controle" type="date" value={form.controle_expiration} onChange={(e) => set("controle_expiration", e.target.value)} /></div>
            </div>
            <Button data-testid="eq-submit" type="submit" className="w-full h-14 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400">ENREGISTRER</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
